import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";

import CustomScrollbars from "components/CustomScrollbars";

// Spinner arrows on number fields collide with the dialog's compact rows.
const NUMBER_INPUT_SX = {
  "& input[type=number]": { MozAppearance: "textfield" as const },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    { WebkitAppearance: "none", margin: 0 },
} as const;

interface AppDialogProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  showCloseIcon?: boolean;
  /** Keep the dialog windowed on phones (default: it goes full screen). */
  disableMobileFullScreen?: boolean;
  /**
   * Width below which the dialog goes full screen. "sm" (the default) keeps
   * every other page as it is; a dialog dense enough to need the whole tablet
   * opts into "md".
   */
  fullScreenBelow?: "sm" | "md";
  /** Stack above another modal, e.g. a dialog opened from a file preview. */
  zIndex?: number;
}

export default function AppDialog({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = "xs",
  showCloseIcon = false,
  disableMobileFullScreen = false,
  fullScreenBelow = "sm",
  zIndex,
}: AppDialogProps) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down(fullScreenBelow));
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={isNarrow && !disableMobileFullScreen}
      sx={zIndex != null ? { zIndex } : undefined}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {/* flex:1 so a title with its own right-aligned controls can spread. */}
          <Box sx={{ flex: 1, minWidth: 0 }}>{title}</Box>
          {showCloseIcon && (
            <IconButton onClick={onClose} size="small" aria-label="Close">
              <Close />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <Divider />
      {/* The content scrolls through CustomScrollbars, like every other
          scrollable surface in the platform, so DialogContent itself must not
          take the overflow - it only frames the area. */}
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          minHeight: 0,
          overflow: "hidden",
          ...NUMBER_INPUT_SX,
        }}
      >
        <CustomScrollbars>
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>{children}</Box>
        </CustomScrollbars>
      </DialogContent>
      {actions != null && (
        <>
          <Divider />
          <DialogActions
            sx={{
              px: 3,
              py: 2,
              flexWrap: "wrap",
              rowGap: 1,
              pb: "calc(16px + env(safe-area-inset-bottom, 0px))",
            }}
          >
            {actions}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
